import MachineTotalExpense from '../readMachineInvoice/machineTotalExpense';
import { CustomErrorLogger } from '../../../../utils/CustomErrorLogger';
import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import { AddInvoiceDialoge } from '../../../invoices/addInvoiceDialog';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import InvoicesFiltration from '../../../invoices/invoiceFiltration';
import InvoicesListView from '../../../invoices/invoicesListView';
import { ListNavigationProvider, useListPageNavigation } from '../../../../hooks/listNavigation';
import { useState, useCallback, useEffect } from 'react';
import MachineInvoiceFrequence from './machineInvoiceFrequence';
import { isObjectEmpty } from '../../../../utils/isObjectEmpty';
import MachineInvoiceChart from './machineInvoiceChart';
import { useSelector } from 'react-redux';
import NoRecord from '../../../NoRecord';
import Iconify from '../../../Iconify';
import axios from 'axios';

const ReadMachineInvoicesTab = () => {

    const machineDetailsData = useSelector((state) => state.MachineDetailsData);
    const hasInvoiceCreatePermission = useHasPermission('facture_create');

    const limit = 30;
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [totalInvoices, setTotalInvoices] = useState('');
    const [invoicesFetched, setInvoicesFetched] = useState(0);
    const [openAddInvoice, setOpenAddInvoice] = useState(false);
    const [filteredInvoicesCount, setFilteredInvoicesCount] = useState(0);
    const [invoiceFilters, setInvoicefilters] = useState(() => {
        const stored = JSON.parse(localStorage.getItem('machineInvoiceFilters')) || {};
        return {
            debouncedSearch: '',
            amount: null,
            company: '',
            date: null,
            ...stored,
            machine_id: machineDetailsData.id,
        };
    });
    const [sortBy, setSortBy] = useState(
        JSON.parse(localStorage.getItem('machineInvoiceSortBy')) ||
        {
            date: null,
            invoice_number: null,
            company: null,
            amount: null,
        }
    );
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'machine-invoices',
        pathname: String(machineDetailsData?.id || ''),
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: invoiceFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });

    const filtersEmpty = isObjectEmpty(invoiceFilters, ['machine_id']);

    useEffect(() => {
        localStorage.setItem('machineInvoiceFilters', JSON.stringify(invoiceFilters));
    }, [invoiceFilters]);

    useEffect(() => {
        localStorage.setItem('machineInvoiceSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    const fetchInvoices = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('invoice', { params: { offset: adjustedOffset, limit, filters: invoiceFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedInvoices = [];
                // Update the fetched records in their correct positions
                response?.data?.invoicesData?.forEach((file, index) => { updatedInvoices[adjustedOffset + index] = file });
                // Update the state with the new files array
                setLoading(false);
                setInvoices(updatedInvoices);
                setFilteredInvoicesCount(response?.data?.filteredinvoicesCount);
                setTotalInvoices(response?.data?.totalInvoicesCount);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching invoices:', error);
            CustomErrorLogger(error, 'fetch invoices in invoices page');
        }
    }, [offset, invoiceFilters, sortBy]);

    const fetchInvoicesFiltrationArrays = useCallback(async () => {
        try {
            const response = await axios.get('invoice/getInvoiceFiltrationArrays', { params: { invoiceFilters } });
            if (response.status === 200 || response.status === 201) {
                setCompanies(response?.data?.companyArray);
            }
        } catch (error) {
            console.error('Error Fetching invoices filtration arrays:', error);
            CustomErrorLogger(error, 'fetch invoices filtration arrays in invoices page');
        }
    }, [invoiceFilters]);

    useEffect(() => {
        fetchInvoicesFiltrationArrays()
    }, [fetchInvoicesFiltrationArrays]);

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices]);

    return (
        <ListNavigationProvider value={navigationValue}>
        <Box>
            {(hasInvoiceCreatePermission && !machineDetailsData?.is_archived && totalInvoices > 0) &&
                <Box className='invoice_headre_action_box' sx={{ marginBottom: '10px' }} onClick={() => setOpenAddInvoice(true)}>
                    <Iconify className='invoice_headre_add_icon' icon={"ic:baseline-plus"} />
                    <Typography className='invoice_headre_action_title'>Ajouter une facture</Typography>
                </Box>
            }
            {machineDetailsData.invoice_machine.length > 0 ?
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4} height={350}>
                            <MachineTotalExpense machineDetailsData={machineDetailsData} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} height={350}>
                            <MachineInvoiceChart machineDetailsData={machineDetailsData} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} height={350}>
                            <MachineInvoiceFrequence machineDetailsData={machineDetailsData} />
                        </Grid>
                    </Grid>
                    {loading ?
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '80vh',
                            }}
                        >
                            <CircularProgress
                                sx={{
                                    '& .MuiCircularProgress-circle': { stroke: '#EA5E00' },
                                }}
                            />
                        </Box> :
                        <Box>
                            <InvoicesFiltration
                                filtersEmpty={filtersEmpty}
                                invoiceFilters={invoiceFilters}
                                setInvoicefilters={setInvoicefilters}
                                totalInvoices={totalInvoices}
                                invoicesFetched={invoicesFetched}
                                filteredInvoicesCount={filteredInvoicesCount}
                            />
                            <InvoicesListView
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                scrollRef={scrollRef}
                                invoices={invoices}
                                setOffset={setOffset}
                                companies={companies}
                                invoiceFilters={invoiceFilters}
                                setInvoicefilters={setInvoicefilters}
                                setInvoicesFetched={setInvoicesFetched}
                                filteredInvoicesCount={filteredInvoicesCount}
                            />
                        </Box>
                    }
                </Box>
                :
                <NoRecord
                    description='Aucun frais ajouté pour le moment'
                    buttonTitle='Ajouter une facture'
                    handleOpenDialog={() => setOpenAddInvoice(true)}
                    hasPermission={hasInvoiceCreatePermission && !machineDetailsData?.is_archived}
                />
            }
            <AddInvoiceDialoge
                openAddInvoice={openAddInvoice}
                setOpenAddInvoice={setOpenAddInvoice}
                machine_ids={[machineDetailsData.id]}
            />
        </Box>
        </ListNavigationProvider>
    )
}

export default ReadMachineInvoicesTab
